import React from "react";
import { FaCalendarAlt, FaEye, FaEyeSlash, FaFileExcel } from "react-icons/fa";

import Spinner from "../../../components/Spinner";
import { exportYearPlannerToExcel } from "../yearPlannerExcelExport";

type IconComponent = React.ComponentType<{ className?: string }>;

const CalendarAltIcon = FaCalendarAlt as IconComponent;
const EyeIcon = FaEye as IconComponent;
const EyeSlashIcon = FaEyeSlash as IconComponent;
const FileExcelIcon = FaFileExcel as IconComponent;

const YearPlannerTab = ({
  t,
  selectedSchoolYear,
  schoolYearLabel,
  eventsLoading,
  yearPlannerMonths,
  canAdd,
  startYearPlannerSelection,
  updateYearPlannerSelection,
  finishYearPlannerSelection,
  cancelYearPlannerSelection,
  openCreateForm,
  isDateInYearPlannerSelection,
  eventId,
  eventScope,
  eventClassroomNames,
  classroomLookup,
  scopeLabels,
  formatEventTime,
  canEdit,
  openEditForm,
}: any) => {
  const schoolYear = selectedSchoolYear ? schoolYearLabel(selectedSchoolYear) : "";
  const canExport = Boolean(selectedSchoolYear && yearPlannerMonths.length > 0 && !eventsLoading);

  const handleExportExcel = () => {
    if (!canExport) return;
    exportYearPlannerToExcel({ yearPlannerMonths, schoolYear, classroomLookup });
  };

  return (
    <section className="w-full min-w-0 max-w-full overflow-x-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="flex flex-col gap-3 border-b border-slate-100 bg-gradient-to-r from-indigo-50/80 to-sky-50/70 px-5 py-5 dark:border-slate-800 dark:from-indigo-950/35 dark:to-sky-950/25 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("schoolCalendar.tabs.yearPlanner", { defaultValue: "Year planner" })}
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {t("schoolCalendar.tabs.yearPlannerIntro", {
              defaultValue:
                "Responsive year view. Click one day or drag from a start date to an end date to create a date range.",
            })}
          </p>
        </div>

        {selectedSchoolYear && (
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <span className="inline-flex rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-100">
              {schoolYear}
            </span>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={!canExport}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-900/50"
            >
              <FileExcelIcon className="h-3.5 w-3.5" />
              {t("schoolCalendar.yearPlanner.exportExcel", { defaultValue: "Export to Excel" })}
            </button>
          </div>
        )}
      </div>

      <div className="p-3 sm:p-5">
      {eventsLoading ? (
        <div className="px-6 py-12">
          <Spinner />
        </div>
      ) : yearPlannerMonths.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-indigo-200 bg-indigo-50 px-6 py-10 text-center text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-950/30 dark:text-indigo-100">
          <CalendarAltIcon className="mx-auto mb-3 text-3xl" />
          <p className="font-semibold">
            {t("schoolCalendar.yearPlanner.noSchoolYearRange", {
              defaultValue: "The selected school year has no usable start and end date.",
            })}
          </p>
        </div>
      ) : (
        <div className="mt-5 w-full max-w-full overflow-x-hidden pb-3">
          <div className="grid w-full min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,22rem),1fr))] gap-3">
            {yearPlannerMonths.map((month: any) => (
              <div
                key={month.key}
                className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <div className="border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-sky-50 px-3 py-2 text-center text-sm font-extrabold capitalize text-gray-900 dark:border-slate-700 dark:from-indigo-950/50 dark:to-sky-950/40 dark:text-gray-100">
                  {month.label}
                </div>

                <div className="grid grid-cols-[2.25rem_2.5rem_minmax(0,1fr)] border-b border-gray-900 bg-white text-center text-[11px] font-bold uppercase text-gray-800 dark:border-slate-700 dark:from-indigo-950/50 dark:to-sky-950/40 dark:text-gray-200">
                  <div className="border-r border-gray-900 px-1 py-1 dark:border-gray-600">
                    {t("schoolCalendar.yearPlanner.weekday", { defaultValue: "Day" })}
                  </div>
                  <div className="border-r border-gray-900 px-1 py-1 dark:border-gray-600">#</div>
                  <div className="px-2 py-1 text-left">
                    {t("schoolCalendar.yearPlanner.planning", { defaultValue: "Planning" })}
                  </div>
                </div>

              <div>
                {month.days.map((day: any) => {
                  const rowTone = day.hasHoliday
                    ? "bg-lime-100 hover:bg-lime-200 dark:bg-lime-950/50 dark:hover:bg-lime-900/60"
                    : day.isWeekend
                      ? "bg-orange-50 hover:bg-orange-100 dark:bg-orange-950/30 dark:hover:bg-orange-900/40"
                      : "bg-sky-50 hover:bg-sky-100 dark:bg-sky-950/30 dark:hover:bg-sky-900/40";

                  return (
                    <div
                      key={day.key}
                      role={canAdd ? "button" : undefined}
                      tabIndex={canAdd ? 0 : undefined}
                      onPointerDown={(pointerEvent) => startYearPlannerSelection(day.key, pointerEvent)}
                      onPointerEnter={() => updateYearPlannerSelection(day.key)}
                      onPointerUp={() => finishYearPlannerSelection(day.key)}
                      onPointerCancel={cancelYearPlannerSelection}
                      onKeyDown={(keyEvent) => {
                        if (canAdd && (keyEvent.key === "Enter" || keyEvent.key === " ")) {
                          keyEvent.preventDefault();
                          openCreateForm(day.key);
                        }
                      }}
                      className={`grid min-h-[2rem] w-full select-none grid-cols-[2.25rem_2.5rem_minmax(0,1fr)] border-b border-gray-900 text-left text-xs transition last:border-b-0 dark:border-gray-600 ${
                        canAdd ? "cursor-pointer" : "cursor-default"
                      } ${
                        isDateInYearPlannerSelection(day.key)
                          ? "ring-2 ring-inset ring-indigo-500 bg-indigo-100 dark:bg-indigo-950/60"
                          : rowTone
                      }`}
                      title={
                        canAdd
                          ? t("schoolCalendar.yearPlanner.addOnDate", {
                              date: day.dateLabel,
                              defaultValue: "Click or drag to block one or more days from {{date}}",
                            })
                          : day.dateLabel
                      }
                    >
                      <div className="flex items-center justify-center border-r border-gray-900 px-1 py-1 text-[11px] font-semibold lowercase text-gray-700 dark:border-gray-600 dark:text-gray-300">
                        {day.weekday}
                      </div>
                      <div className="flex items-center justify-center border-r border-gray-900 px-1 py-1 font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                        {day.dayNumber}
                      </div>
                      <div className="min-w-0 overflow-hidden px-2 py-1">
                        {day.events.length === 0 ? (
                          <span className="text-gray-400 dark:text-gray-500">&nbsp;</span>
                        ) : (
                          <div className="space-y-1">
                            {day.events.map((event: any) => {
                              const id = eventId(event) || `${event?.title}-${event?.startDate}`;
                              const scope = eventScope(event);
                              const classroomNames = eventClassroomNames(event, classroomLookup);
                              const classLabel =
                                scope === "CLASSROOM" && classroomNames.length > 0
                                  ? classroomNames.join(", ")
                                  : scopeLabels[scope] || scope;
                              const published = Boolean(event?.published);
                              const eventTone = ["HOLIDAY", "DAY_OFF"].includes(
                                String(event?.eventType || "").toUpperCase(),
                              )
                                ? "font-extrabold text-gray-950 dark:text-gray-50"
                                : String(event?.eventType || "").toUpperCase() === "STUDY_DAY"
                                  ? "font-semibold text-red-600 dark:text-red-300"
                                  : "font-medium text-gray-800 dark:text-gray-100";

                              const content = (
                                <>
                                  <span className="flex min-w-0 items-center gap-1.5">
                                    <span className={`block min-w-0 flex-1 truncate ${eventTone}`}>
                                      {event?.title || "-"}
                                    </span>
                                    <span
                                      className={`shrink-0 ${
                                        published
                                          ? "text-emerald-700 dark:text-emerald-300"
                                          : "text-gray-400 dark:text-gray-500"
                                      }`}
                                      title={
                                        published
                                          ? t("schoolCalendar.published", { defaultValue: "Gepubliceerd" })
                                          : t("schoolCalendar.unpublished", { defaultValue: "Niet gepubliceerd" })
                                      }
                                    >
                                      {published ? (
                                        <EyeIcon className="h-3 w-3" />
                                      ) : (
                                        <EyeSlashIcon className="h-3 w-3" />
                                      )}
                                    </span>
                                  </span>
                                  <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                                    <span className="truncate">{formatEventTime(event, t)}</span>
                                    {scope === "CLASSROOM" && (
                                      <span className="truncate rounded-full bg-sky-100 px-1.5 py-0.5 text-sky-800 dark:bg-sky-900/60 dark:text-sky-100">
                                        {classLabel}
                                      </span>
                                    )}
                                  </span>
                                </>
                              );

                              return canEdit ? (
                                <button
                                  key={String(id)}
                                  type="button"
                                  onPointerDown={(pointerEvent) => {
                                    pointerEvent.stopPropagation();
                                  }}
                                  onClick={(clickEvent) => {
                                    clickEvent.stopPropagation();
                                    openEditForm(event);
                                  }}
                                  className="block w-full rounded-md px-1.5 py-1 text-left transition hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:hover:bg-slate-800/80"
                                  title={`${event?.title || "-"} — ${day.dateLabel}`}
                                >
                                  {content}
                                </button>
                              ) : (
                                <div
                                  key={String(id)}
                                  className="rounded-md px-1.5 py-1"
                                  title={`${event?.title || "-"} — ${day.dateLabel}`}
                                >
                                  {content}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
      </div>
    </section>
  );
};

export default YearPlannerTab;
